import type { Supplier } from "@/apis/gql/types";
import { DateCell } from "@/components/shared/cells/date-cell";
import { SelectCell } from "@/components/shared/cells/select";
import { SupplierCell } from "@/components/shared/cells/supplier";
import {
  type DatevError,
  DatevErrorMessage,
} from "@/components/shared/errors/datev-error-message";
import { ServerErrorMessage } from "@/components/shared/errors/server-error-message";
import { ExportsValidationErrors } from "@/components/shared/exports/validation-errors";
import { SelectHeader } from "@/components/shared/select-inputs/select";
import { WarningIcon } from "@/components/shared/status/icons";
import { DataTableColumnHeader } from "@/components/shared/tables/ColumnHeader";
import { Badge } from "@/components/ui/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { StatusBadge } from "@/features/invoices-table/cells/status-badge";
import type { ColumnDef, Row } from "@tanstack/react-table";
import {
  Check,
  ChevronDown,
  ChevronRight,
  CircleCheck,
  Download,
  Eye,
} from "lucide-react";
import { useTranslation } from "react-i18next";

const selectColumn: ColumnDef<Supplier> = {
  id: "select",
  size: 45,
  header: (header) => <SelectHeader {...header} />,
  cell: (cell) => (
    <div className="flex items-center justify-center h-full w-full">
      <SelectCell {...cell} />
    </div>
  ),
  enableSorting: false,
  enableHiding: false,
};

const supplierNameColumn: ColumnDef<Supplier> = {
  accessorKey: "supplierName",
  size: 500,
  header: ({ column }) => (
    <DataTableColumnHeader column={column} title="Name" />
  ),
  cell: ({ row }) => {
    const { t } = useTranslation();

    const name = row.getValue("supplierName") as string;
    const address = row.original.address ?? undefined;

    return (
      <div
        className="flex-1 flex flex-row gap-2"
        style={{
          paddingLeft: `${row.depth * 2}rem`,
        }}
      >
        {row.getCanExpand() && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              row.getToggleExpandedHandler()();
            }}
            style={{ cursor: "pointer" }}
          >
            {row.getIsExpanded() ? (
              <ChevronDown size={18} />
            ) : (
              <ChevronRight size={18} />
            )}
          </button>
        )}
        {row.depth > 0 ? (
          <Badge variant="secondary" className="px-1">
            {t("suppliers.sub")}
          </Badge>
        ) : row.getCanExpand() ? (
          <Badge variant="secondary" className="px-1">
            {t("suppliers.parent")}
          </Badge>
        ) : null}
        <div className="w-full overflow-hidden">
          <SupplierCell name={name} address={address} />
        </div>
      </div>
    );
  },
};

const datevAccountNumberColumn = (
  type: "creditor" | "debtor",
): ColumnDef<Supplier> => ({
  accessorKey: "datevAccountNumber",
  size: 150,
  header: ({ column }) => {
    const { t } = useTranslation();
    return (
      <DataTableColumnHeader
        column={column}
        title={
          {
            creditor: t("creditors.datev_account_number"),
            debtor: t("debtors.datev_account_number"),
          }[type]
        }
      />
    );
  },
});

const customerNumberColumn: ColumnDef<Supplier> = {
  accessorKey: "customerNumber",
  size: 150,
  header: ({ column }) => {
    const { t } = useTranslation();
    return (
      <DataTableColumnHeader column={column} title={t("customer_number")} />
    );
  },
};

const createdAtColumn: ColumnDef<Supplier> = {
  accessorKey: "createdAt",
  size: 150,
  header: ({ column }) => (
    <DataTableColumnHeader column={column} title="Erstell.Datum" />
  ),
  cell: ({ row }) => {
    return <DateCell value={new Date(row?.original?.createdAt)} />;
  },
};

const statusLogic = (row: Row<Supplier>) => {
  const status = row.original.status;
  const exportedAt = row.original.exportedAt;
  const imported = row.original.imported;

  let config = {
    icon: Check,
    label: "Geprüft",
    value: "confirmed",
    iconColor: "text-green-800",
    textColor: "text-green-800",
    containerColor: "bg-green-100 border border-green-200",
  };

  if (imported) {
    config = {
      icon: Download,
      label: "Importiert",
      value: "imported",
      iconColor: "text-gray-800",
      textColor: "text-gray-800",
      containerColor: "bg-gray-100 border border-gray-200",
    };
  } else if (exportedAt !== null) {
    config = {
      icon: CircleCheck,
      label: "Exportiert",
      value: "exported",
      iconColor: "text-indigo-800",
      textColor: "text-indigo-800",
      containerColor: "bg-indigo-100 border border-indigo-200",
    };
  } else if (status === "NEW") {
    config = {
      icon: Eye,
      label: "Zu Prüfen",
      value: "new",
      iconColor: "text-orange-600",
      textColor: "text-orange-800",
      containerColor: "bg-orange-100 border border-orange-200",
    };
  }

  return config;
};

const statusColumn: ColumnDef<Supplier> = {
  accessorKey: "status",
  filterFn: (row, columnId, value) => {
    if (value.length === 0) return true;

    const config = statusLogic(row);

    return value.includes(config.value);
  },
  header: ({ column }) => (
    <DataTableColumnHeader column={column} title="Status" />
  ),
  cell: ({ row }) => {
    const config = statusLogic(row);

    if (row.original.parentSupplierId) return null;

    return (
      <StatusBadge
        icon={config.icon}
        iconColor={config.iconColor}
        textColor={config.textColor}
        containerColor={config.containerColor}
      >
        {config.label}
      </StatusBadge>
    );
  },
};

const exportStatusColumn: ColumnDef<Supplier> = {
  accessorKey: "exportStatus",
  size: 40,
  header: ({ column }) => null,
  cell: ({ row }) => {
    const exportedSuppliers = row.original.exportErrors?.supplierIds ?? [];

    const exportError = row.original.exportErrors?.results?.errors;

    const invoiceValidationErrors = row.original.validationErrors;

    if (
      !exportedSuppliers.includes(row.original.supplierId) ||
      (!exportError && !invoiceValidationErrors)
    ) {
      return null;
    }

    const datevErrors = exportError.validation_details;

    const renderErrors = () => {
      if (invoiceValidationErrors) {
        return <ExportsValidationErrors errors={invoiceValidationErrors} />;
      }

      if (datevErrors) {
        return <DatevErrorMessage datevError={datevErrors as DatevError} />;
      }

      return (
        <ServerErrorMessage
          code={exportError.code || exportError.data?.error}
          uri={exportError.data?.error_uri}
        />
      );
    };

    return (
      <div className="text-center">
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger>
              <WarningIcon />
            </TooltipTrigger>
            <TooltipContent>{renderErrors()}</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
    );
  },
};

export function buildColumns(
  type: "creditor" | "debtor",
): ColumnDef<Supplier>[] {
  return [
    selectColumn,
    supplierNameColumn,
    datevAccountNumberColumn(type),
    ...(type === "debtor" ? [customerNumberColumn] : []),
    createdAtColumn,
    statusColumn,
    exportStatusColumn,
  ];
}
